import { memo, useCallback, useEffect, useRef } from 'react';

import { TModalSize } from '~/shared/hooks/useModal';
import { useFocusTrap } from '~/shared/hooks/useFocusTrap';
import { useClickOutside } from '~/shared/hooks/useClickOutside';
import { useClickEsc } from '~/shared/hooks/useClickEsc';

import { WithChildren } from '~/shared/types/WithChildren';

import { Overlay, Content } from './styles';

import { ModalHeader } from './ModalHeader';

export interface IModalProps {
  id?: string;
  onClose?(): void;
  title?: string;
  size?: TModalSize;
  closeOnClickOutside?: boolean;
  isTopMost?: boolean;
}

export const closeModal = (id: string): void => {
  const overlay = document.getElementById(`${id}-overlay`);
  const content = document.getElementById(`${id}-content`);

  if (overlay) {
    overlay.style.opacity = '0';
  }

  if (content) {
    content.style.opacity = '0';
    content.style.transform = 'translateY(36px)';
  }
};

export const Modal: WithChildren<IModalProps> = memo(
  ({
    onClose = () => null,
    children,
    title,
    id,
    size = 'sm',
    closeOnClickOutside = true,
    isTopMost = true,
  }) => {
    const overlayRef = useRef<HTMLDivElement>(null);
    const contentRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
      setTimeout(() => {
        if (overlayRef.current) {
          overlayRef.current.style.opacity = '1';
        }

        if (contentRef.current) {
          contentRef.current.style.opacity = '1';
          contentRef.current.style.transform = 'translateY(0)';
        }
      }, 1);
    }, []);

    const close = useCallback(() => {
      if (overlayRef.current) {
        overlayRef.current.style.opacity = '0';
      }

      if (contentRef.current) {
        contentRef.current.style.opacity = '0';
        contentRef.current.style.transform = 'translateY(36px)';
      }

      setTimeout(() => {
        onClose();
      }, 1);
    }, [onClose]);

    const handleClose = useCallback(
      (closeOnX?: boolean) => {
        if (closeOnClickOutside || closeOnX) {
          close();
        }
      },
      [close, closeOnClickOutside]
    );

    // Only the top-most modal should react to global Esc/click-outside listeners;
    // otherwise stacking a modal on top of another would close both at once.
    // A Select rendered inside the modal portals its dropdown out of `Content`,
    // so clicking an option must not be read as a click outside the modal.
    const containerRef = useClickOutside<HTMLDivElement>(
      handleClose,
      isTopMost,
      { ignorePortalRoot: true }
    );

    useClickEsc(close, isTopMost);
    useFocusTrap(containerRef, isTopMost);

    const titleId = id ? `${id}-title` : undefined;

    return (
      <Overlay ref={overlayRef} id={`${id}-overlay`} size={size}>
        <div ref={contentRef} id={`${id}-content`}>
          <Content
            ref={containerRef}
            role="dialog"
            aria-modal="true"
            {...(title && titleId ? { 'aria-labelledby': titleId } : {})}
            tabIndex={-1}
          >
            {!!title && (
              <ModalHeader id={titleId} title={title} onClose={handleClose} />
            )}

            {children}
          </Content>
        </div>
      </Overlay>
    );
  }
);
